import { useState, useEffect } from 'react';
import { stopMediaStream } from './utils';

const useMicrophoneVolume = (deviceId: string) => {
  const [volume, setVolume] = useState(0);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    let audioContext: AudioContext;
    let analyser: AnalyserNode;
    let microphone: MediaStreamAudioSourceNode;
    let animationFrameId: number;

    const initAudio = async (deviceId: string) => {
      if (audioContext) {
        audioContext.close();
      }
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;

      try {
        const constraints =
          deviceId && deviceId !== 'default' ?
            { audio: { deviceId: { exact: deviceId } } } :
            { audio: true };
        activeStream && stopMediaStream(activeStream);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setActiveStream(stream);
        if (audioContext.state === 'closed') {
          return;
        }
        microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const loop = () => {
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const average = sum / dataArray.length;
          const normalizedVolume = Math.min(Math.max(0, (average / 255) * 100), 100);
          setVolume(normalizedVolume);
          animationFrameId = requestAnimationFrame(loop);
        };

        loop();
      } catch (error) {
        console.error("Error accessing the microphone", error);
      }
    };

    initAudio(deviceId);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (audioContext) {
        audioContext.close();
      }
      activeStream && stopMediaStream(activeStream);
    };

    // eslint-disable-next-line
  }, [deviceId]);

  return volume;
};

export default useMicrophoneVolume;